import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";

import type { TimeAttendanceStepItem } from "@/types/timeAttendance";
import TimeAttendanceRuleFinalStep from "./time-attendance-rule-final-step";
import TimeAttendanceRuleStep1 from "./time-attendance-rule-step1";
import TimeAttendanceRuleStep2 from "./time-attendance-rule-step2";
import TimeAttendanceRuleShareLocationSteps from "./time-attendance-rule-share-location-steps";
import TimeAttendanceRuleCapturePhotoSteps from "./time-attendance-rule-capture-photo-steps";
import type { IAttendanceExceptionEligibility } from "@/types/(my-space)/timeAttendace.interface";
import type { ILocationFix } from "@/utils/geolocation";
import { Button } from "../ui/button";
import { useCreateAttendanceExceptionMutation } from "@/services/(my-space)/api.time-attendance";
import { startCase } from "lodash";
import { ENUM_ATTENDANCE_REQUEST_TYPE } from "@/utils/enums/time-attendance.enum";
import { format } from "date-fns";
import { Hourglass } from "lucide-react";
import Countdown from "react-countdown";

interface Props {
  open: boolean;
  onClose: () => void;
  eligibility?: IAttendanceExceptionEligibility;
  handleCheckEligibilityAttendanceRule?: (location?: ILocationFix | null) => Promise<void>;
}

const CountdownRenderer = ({
  minutes,
  seconds,
  completed,
}: {
  minutes: number;
  seconds: number;
  completed: boolean;
}) => {
  const { t } = useTranslation(["common", "my-space"]);
  if (completed) {
    return <span>{t("my-space:checkin.expired")}</span>;
  }
  return (
    <span>
      {t("my-space:checkin.expires_in", {
        minutes: minutes.toString().padStart(2, "0"),
        seconds: seconds.toString().padStart(2, "0"),
      })}
    </span>
  );
};

const TimeAttendanceRuleModal = ({
  open,
  onClose,
  eligibility,
  handleCheckEligibilityAttendanceRule,
}: Props) => {
  const { t } = useTranslation(["common", "my-space"]);
  const requestTypeLabel =
    eligibility?.requestType === ENUM_ATTENDANCE_REQUEST_TYPE.CHECK_OUT
      ? t("my-space:time_attendance.check_out")
      : eligibility?.requestType === ENUM_ATTENDANCE_REQUEST_TYPE.CHECK_IN
        ? t("my-space:time_attendance.check_in")
        : startCase(eligibility?.requestType)?.replace(" ", "-");
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({
    location: false,
    photo: false,
  });
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [withoutLocation, setWithoutLocation] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [createAttendanceException, { isLoading: isCreating }] =
    useCreateAttendanceExceptionMutation();

  const isLocationVerified =
    eligibility?.location?.isVerified &&
    eligibility?.location?.userLocation?.latitude;
  const needsAdminApproval =
    !isLocationVerified && eligibility?.location?.userLocation?.latitude;
  const isLocationAccessRequired =
    !isLocationVerified &&
    !eligibility?.location?.userLocation?.latitude &&
    withoutLocation;
  const ruleSummary = eligibility?.timeAttendanceRuleSummary;
  const isLocationRequired =
    ruleSummary &&
    Object.hasOwn(ruleSummary, "isLocationMandatoryCheckinCheckout")
      ? ruleSummary.isLocationMandatoryCheckinCheckout
      : eligibility?.empAttendanceRulesSummary?.method;

  // Validation schema - only require reason if location is not verified
  const validationSchema = Yup.object({
    requestReason:
      needsAdminApproval || isLocationAccessRequired
        ? Yup.string().required(
            t("my-space:checkin.reason_required_when_location_mismatch"),
          )
        : Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      requestReason: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const toastId = "create-attendance-exception-toast";

      try {
        const formData = new FormData();

        // Add expiredAt from eligibility
        if (eligibility?.expiredAt) {
          formData.append("expiredAt", eligibility.expiredAt.toString());
        }

        // Only add reason if location is not verified
        if (
          (needsAdminApproval || isLocationAccessRequired) &&
          values.requestReason
        ) {
          formData.append("requestReason", values.requestReason);
        }

        // Only add photo if captured and photo is required
        if (
          capturedPhoto &&
          !eligibility?.timeAttendanceRuleSummary.isCapturePhotoCheckinCheckout
        ) {
          // Convert base64 to blob
          const blob = await fetch(capturedPhoto).then((r) => r.blob());
          formData.append("file", blob, "attendance-photo.jpg");
        }

        const res = await createAttendanceException(formData).unwrap();
        setSuccessMessage(res.message);
        setCurrentStepIndex(4);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ??
            error?.message ??
            t("my-space:checkin.failed_to_submit_checkin"),
          { id: toastId },
        );
      }
    },
  });

  const steps: TimeAttendanceStepItem[] = [
    {
      id: "step-1",
      title: (
        <div className="flex flex-col gap-1">
          <p className="text-[16px] font-medium">
            {t("my-space:checkin.type_requirements", {
              type: requestTypeLabel,
            })}
          </p>
          <p className="flex gap-1 text-xs text-gray-500">
            {t("my-space:checkin.attempted_type_at", {
              type: requestTypeLabel ?? t("my-space:checkin.time"),
              time: format(eligibility?.attemptedTime ?? new Date(), "hh:mm a"),
            })}
          </p>
        </div>
      ),
      component: TimeAttendanceRuleStep1,
    },
    {
      id: "step-2",
      title: (
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-">
            {" "}
            <p className="text-[16px] font-medium">
              {t("my-space:checkin.complete_required_steps")}
            </p>
            <p className="flex gap-1 text-xs text-gray-500">
              {/* One interpolated sentence — Arabic orders these differently. */}
              {t("my-space:checkin.attempted_type_at", {
                type: requestTypeLabel ?? t("my-space:checkin.time"),
                time: format(
                  eligibility?.attemptedTime ?? new Date(),
                  "hh:mm a",
                ),
              })}
            </p>
          </div>

          <div className="flex justify-center items-center">
            <p className="flex justify-center items-center px-4 py-1.5 rounded-full bg-amber-200 text-amber-600 text-xs font-semibold">
              <Hourglass size={16} className="inline me-1" />{" "}
              <Countdown
                date={
                  eligibility?.expiredAt
                    ? new Date(eligibility.expiredAt)
                    : new Date()
                }
                renderer={CountdownRenderer}
              />
            </p>
          </div>
        </div>
      ),
      component: TimeAttendanceRuleStep2,
    },
    {
      id: "location-step",
      title: (
        <div className="flex justify-between items-center">
          <p className="text-[16px] font-medium">
            {t("my-space:checkin.share_location")}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentStepIndex(1)}
          >
            {t("common:buttons.back")}
          </Button>
        </div>
      ),
      component: TimeAttendanceRuleShareLocationSteps,
    },
    {
      id: "photo-step",
      title: (
        <div className="flex justify-between items-center">
          <p className="text-[16px] font-medium">
            {t("my-space:checkin.capture_photo")}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentStepIndex(1)}
          >
            {t("common:buttons.back")}
          </Button>
        </div>
      ),
      component: TimeAttendanceRuleCapturePhotoSteps,
    },
    {
      id: "final-step",
      component: TimeAttendanceRuleFinalStep,
    },
  ];

  useEffect(() => {
    if (!open) {
      setCurrentStepIndex(0);
      setCompletedSteps({ location: false, photo: false });
      setCapturedPhoto(null);
      setWithoutLocation(false);
      formik.resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !eligibility?.expiredAt) return;

    const expiryTime = new Date(eligibility.expiredAt).getTime();
    const now = Date.now();
    const remainingTime = expiryTime - now;

    if (remainingTime <= 0) {
      const delayTimer = setTimeout(() => {
        onClose();
      }, 5000);

      return () => clearTimeout(delayTimer);
    }

    const timer = setTimeout(() => {
      onClose();
    }, remainingTime + 3000);

    return () => clearTimeout(timer);
  }, [open, eligibility?.expiredAt, onClose]);

  if (!steps.length) return null;

  const currentStep = steps[currentStepIndex];
  const CurrentStepComponent = currentStep.component;
  const isFinalStep = currentStep.id === "final-step";

  const handleNext = () => {
    const currentId = steps[currentStepIndex].id;

    // Handle location step completion
    if (currentId === "location-step") {
      setCompletedSteps((prev) => ({ ...prev, location: true }));
      setCurrentStepIndex(1);
      return;
    }

    // Handle photo step completion
    if (currentId === "photo-step") {
      setCompletedSteps((prev) => ({ ...prev, photo: true }));
      setCurrentStepIndex(1);
      return;
    }

    // Handle step-2 completion (when both tasks are done or not required)
    if (
      currentId === "step-2" &&
      (completedSteps.location ||
        isLocationVerified ||
        needsAdminApproval ||
        isLocationAccessRequired ||
        !isLocationRequired) &&
      (completedSteps.photo ||
        (eligibility?.timeAttendanceRuleSummary &&
          !Object.hasOwn(
            eligibility?.timeAttendanceRuleSummary,
            "isCapturePhotoCheckinCheckout",
          )))
    ) {
      formik.handleSubmit();
      return;
    }

    // Normal flow for step-1
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      onClose();
    }
  };

  const handleReject = () => {
    onClose();
  };

  const handleInteractOutside = (e: Event) => {
    if (isFinalStep) {
      onClose();
    } else {
      e.preventDefault();
    }
  };

  const handleStartLocation = () => {
    setCurrentStepIndex(2);
  };
  const handleStartPhoto = () => {
    setCurrentStepIndex(3);
  };

  return (
    <Dialog open={open}>
      <DialogContent
        showCloseButton={false}
        className="max-w-md p-0 gap-0 overflow-hidden"
        onPointerDownOutside={handleInteractOutside}
        onEscapeKeyDown={handleInteractOutside}
      >
        {currentStep.title && (
          <DialogHeader className="p-5 bg-gray-100 shadow-xs">
            <DialogTitle>{currentStep.title}</DialogTitle>
          </DialogHeader>
        )}

        <div>
          <CurrentStepComponent
            onNext={handleNext}
            onReject={handleReject}
            onStartLocation={handleStartLocation}
            onStartPhoto={handleStartPhoto}
            completedSteps={completedSteps}
            eligibility={eligibility}
            handleCheckEligibilityAttendanceRule={
              handleCheckEligibilityAttendanceRule
            }
            formik={formik}
            capturedPhoto={capturedPhoto}
            setCapturedPhoto={setCapturedPhoto}
            isSubmitting={isCreating}
            setWithoutLocation={setWithoutLocation}
            withoutLocation={withoutLocation}
            successMessage={successMessage}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TimeAttendanceRuleModal;
